import moment from "moment";
import { createStructuredSelector, createSelector } from "reselect";
import { workareaSelector } from "features/workarea/selectors";
import { organizationSelector } from "features/organization/selectors";
import { siteDataSelector } from "../../../app/sitedata/selectors";

export const filesFetchingSelector = state =>
  state.ctd.getIn(["annotate", "filesFetching"]);
export const filesDidInvalidateSelector = state =>
  state.ctd.getIn(["annotate", "filesDidInvalidate"]);

export const filesBeingSaved = state =>
  state.ctd.getIn(["annotate", "filesBeingSaved"]);
export const filesBeingSavedSelector = createSelector(filesBeingSaved, x =>
  x.toJS()
);
export const savingFilesSelector = createSelector(
  filesBeingSaved,
  x => !x.isEmpty()
);

export const successfulAnnotations = state =>
  state.ctd.getIn(["annotate", "successfulAnnotations"]);

export const successfulAnnotationsSelector = createSelector(
  successfulAnnotations,
  x => x.toJS()
);

export const filesToAnnotate = state =>
  state.ctd.getIn(["annotate", "filesToAnnotate"]);
export const filesToAnnotateSelector = createSelector(filesToAnnotate, x =>
  x.toJS()
);
export const filesListSelector = createSelector(filesToAnnotate, x =>
  x
    .toList()
    .sortBy(a => moment(a.get("collectionDt") || "1990-01-01")) // sort no date casts to the bottom
    .reverse()
    .toJS()
    .map(a => {
      // eslint-disable-next-line no-param-reassign
      a.disabled = a.collectionDt === null || !a.s3Verified; // can't annotate files if cast detection failed or rsk/hex file not uploaded
      return a;
    })
);

export const wantedFilePks = state =>
  state.ctd.getIn(["annotate", "filesBeingAnnotated"]).toSet();
export const filesBeingAnnotatedSelector = createSelector(wantedFilePks, x =>
  x.toJS()
);

export const fetchedFilePks = state =>
  state.ctd
    .getIn(["annotate", "castsInFiles"])
    .keySeq()
    .map(x => parseInt(x, 10))
    .toSet();
export const fetchedFilePksSelector = createSelector(fetchedFilePks, x =>
  x.toJS()
);

export const fetchingFilePks = createSelector(
  [wantedFilePks, fetchedFilePks],
  (w, f) => w.subtract(f)
);
export const fetchingFilePksSelector = createSelector(fetchingFilePks, x =>
  x.toJS()
);

export const fetchedAndWantedPks = createSelector(
  [wantedFilePks, fetchedFilePks],
  (w, f) => w.intersect(f)
);
export const fetchedFilesBeingAnnotatedSelector = createSelector(
  fetchedAndWantedPks,
  x => x.toJS()
);

export const castsInFiles = state =>
  state.ctd.getIn(["annotate", "castsInFiles"]);
export const castsInFilesSelector = createSelector(castsInFiles, x => x.toJS());

export const castsInFilesBeingAnnotatedSelector = createSelector(
  [fetchedFilesBeingAnnotatedSelector, castsInFilesSelector],
  (fetched, casts) =>
    fetched
      // only keep the fetched filePks that have casts
      .filter(filePk => casts[filePk])
      // for each file, add the casts to the array as objects indexed by castpk
      .reduce((R, x) => {
        Object.keys(casts[x]).forEach(y => {
          const cast = {};
          cast[y] = casts[x][y];
          R.push(cast);
        });
        return R;
      }, [])
      // sort the casts based on their collection time
      .sort(
        (a, b) =>
          moment(a[Object.keys(a)[0]].startDt) -
          moment(b[Object.keys(b)[0]].startDt)
      )
      // return an object indexed by castpk
      /* eslint-disable no-param-reassign */
      .reduce((R, x) => {
        const key = Object.keys(x)[0];
        R[key] = x[key];
        return R;
      }, {})
);

export const castsInFilesBeingAnnotatedListSelector = createSelector(
  [fetchedFilesBeingAnnotatedSelector, castsInFilesSelector],
  (fetched, casts) =>
    fetched
      .reduce((R, x) => {
        Object.keys(casts[x]).forEach(y => {
          R.push(casts[x][y]);
        });
        return R;
      }, [])
      .sort((a, b) => moment(a.startDt) - moment(b.startDt))
);

export const castsFetchingSelector = createSelector(
  fetchingFilePksSelector,
  x => x.length > 0
);

export const matchedFormsSelector = createSelector(
  state => state.ctd.getIn(["annotate", "matchedForms"]),
  x => x.toJS()
);
export const matchedFormsBeingAnnotatedSelector = createSelector(
  [castsInFilesBeingAnnotatedListSelector, matchedFormsSelector],
  (casts, allForms) => {
    const castPks = casts.map(c => c.ctdCastPk);
    return (
      Object.keys(allForms)
        // double check what is stored as string or int (ie do tests match reality)
        .map(x => parseInt(x, 10))
        .filter(key => castPks.includes(key))
        .reduce((obj, key) => {
          obj[key] = allForms[key]; // eslint-disable-line no-param-reassign
          return obj;
        }, {})
    );
  }
);

export const duplicateFormsInCastsBeingAnnotatedSelector = createSelector(
  matchedFormsBeingAnnotatedSelector,
  m => {
    const forms = [];
    const duplicates = [];
    Object.keys(m).forEach(x => {
      forms.push(m[x]);
    });

    forms.forEach(f => {
      if (!f.miscast) {
        const matchedForm = forms.filter(x => !x.miscast && f.pk === x.pk);
        if (matchedForm.length > 1) duplicates.push(f.ctdCastPk);
      }
    });
    return duplicates;
  }
);
export const possibleFormsSelector = createSelector(
  state => state.ctd.getIn(["annotate", "possibleForms"]),
  x => x.toJS()
);

export const annotationModalOpenSelector = state =>
  state.ctd.getIn(["annotate", "annotationModalOpen"]);
export const annotationModalSelectedSelector = state =>
  state.ctd.getIn(["annotate", "annotationModalSelected"]);
export const annotationModalCastSelector = createSelector(
  state => state.ctd.getIn(["annotate", "annotationModalCast"]),
  x => x.toJS()
);
export const annotationModalFormsSelector = createSelector(
  state => state.ctd.getIn(["annotate", "annotationModalForms"]),
  x => x.toJS()
);
export const chartModalOpenSelector = state =>
  state.ctd.getIn(["annotate", "chartModalOpen"]);
export const mapModalOpenSelector = state =>
  state.ctd.getIn(["annotate", "mapModalOpen"]);
export const chartModalCastSelector = createSelector(
  state => state.ctd.getIn(["annotate", "chartModalCast"]),
  x => x.toJS()
);
export const mapModalCastSelector = createSelector(
  state => state.ctd.getIn(["annotate", "mapModalCast"]),
  x => x.toJS()
);
export const mapModalFormSelector = createSelector(
  state => state.ctd.getIn(["annotate", "mapModalForm"]),
  x => x.toJS()
);
export const chartDataFetchingSelector = state =>
  state.ctd.getIn(["annotate", "chartDataFetching"]);
export const chartDataSelector = createSelector(
  state => state.ctd.getIn(["annotate", "chartData"]),
  x => x.toJS()
);

export const mapSiteDataSelector = createSelector(
  state => state.ctd.getIn(["annotate", "mapSiteData"]),
  x => x.toJS()
);
export const annotationSnackbarOpenSelector = state =>
  state.ctd.getIn(["annotate", "annotationSnackbarOpen"]);

export default createStructuredSelector({
  filesFetching: filesFetchingSelector,
  castsFetching: castsFetchingSelector,
  fetchedFilePks: fetchedFilePksSelector,
  savingFiles: savingFilesSelector,
  filesDidInvalidate: filesDidInvalidateSelector,
  filesToAnnotate: filesToAnnotateSelector,
  filesList: filesListSelector,
  filesBeingAnnotated: filesBeingAnnotatedSelector,
  castsInFiles: castsInFilesSelector,
  castsInFilesBeingAnnotated: castsInFilesBeingAnnotatedSelector,
  castsInFilesBeingAnnotatedList: castsInFilesBeingAnnotatedListSelector,
  matchedForms: matchedFormsSelector,
  duplicateFormsInCastsBeingAnnotated: duplicateFormsInCastsBeingAnnotatedSelector,
  possibleForms: possibleFormsSelector,
  annotationModalOpen: annotationModalOpenSelector,
  annotationModalCast: annotationModalCastSelector,
  annotationModalForms: annotationModalFormsSelector,
  annotationModalSelected: annotationModalSelectedSelector,
  chartModalOpen: chartModalOpenSelector,
  chartModalCast: chartModalCastSelector,
  chartDataFetching: chartDataFetchingSelector,
  chartData: chartDataSelector,
  mapModalOpen: mapModalOpenSelector,
  mapModalCast: mapModalCastSelector,
  mapModalForm: mapModalFormSelector,
  siteData: siteDataSelector,
  mapSiteData: mapSiteDataSelector,
  annotationSnackbarOpen: annotationSnackbarOpenSelector,
  successfulAnnotations: successfulAnnotationsSelector,
  workarea: workareaSelector,
  organization: organizationSelector
});
